import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { KG } from '../../types';
import CreateKGModal from './CreateKGModal';
import { extractErrorMessage } from '../../utils/errorHandler';
import { relativeTime } from '../../utils/dateUtils';
import '../shared.css';
import './KGList.css';

interface KGWithStats extends KG {
  totalVersions: number;
  publishedVersions: number;
  unpublishedVersions: number;
  latestVersionId?: string;
  latestCreatedAt?: string;
  latestDataVersion?: string;
  gdsEnabled?: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];

interface KGListProps {
  hasGdsLicense: boolean;
}

const KGList: React.FC<KGListProps> = ({ hasGdsLicense }) => {
  const [kgs, setKgs] = useState<KGWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Table state
  const [sortKey, setSortKey] = useState<'kg_name' | 'totalVersions' | 'latestCreatedAt'>('kg_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(0);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const [copyToast, setCopyToast] = useState<string | null>(null);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const loadKGs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.listKGs();
      if (!response.data) return;

      const kgMap = new Map<string, KG>();
      response.data.forEach((item: any) => {
        if (!kgMap.has(item.kg_id)) {
          kgMap.set(item.kg_id, {
            kg_id: item.kg_id,
            kg_name: item.kg_name || item.attributes?.kg_name || item.kg_id,
          });
        }
      });

      const uniqueKGs = Array.from(kgMap.values());
      const kgsWithStats = await Promise.all(
        uniqueKGs.map(async (kg) => {
          try {
            const versionsResponse: any = await apiService.listVersions(kg.kg_id);
            if (versionsResponse.data) {
              const sorted = [...versionsResponse.data].sort((a: any, b: any) =>
                Number(b.version_id) - Number(a.version_id)
              );
              const totalVersions = sorted.length;
              const publishedVersions = sorted.filter((v: any) =>
                v.published ?? v.attributes?.published ?? false
              ).length;
              const latest = sorted[0];
              const gdsEnabled = sorted.some((v: any) =>
                v.gds_enabled ?? v.attributes?.gds_enabled ?? false
              );
              return {
                ...kg,
                totalVersions,
                publishedVersions,
                unpublishedVersions: totalVersions - publishedVersions,
                latestVersionId: latest?.version_id,
                latestCreatedAt: latest?.created_at || latest?.attributes?.created_at,
                latestDataVersion: latest?.data_version || latest?.attributes?.data_version || undefined,
                gdsEnabled,
              };
            }
          } catch {
            // non-fatal
          }
          return { ...kg, totalVersions: 0, publishedVersions: 0, unpublishedVersions: 0, gdsEnabled: false };
        })
      );
      setKgs(kgsWithStats);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load knowledge graphs'));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadKGs();
    setRefreshing(false);
  }, [loadKGs]);

  const handleKGCreated = useCallback(() => {
    setShowCreateModal(false);
    loadKGs();
  }, [loadKGs]);

  useEffect(() => {
    loadKGs();
  }, [loadKGs]);

  // Close overflow menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  const filteredKgs = useMemo(() => {
    const s = searchTerm.toLowerCase();
    return kgs.filter((kg) =>
      kg.kg_name.toLowerCase().includes(s) || kg.kg_id.toLowerCase().includes(s)
    );
  }, [kgs, searchTerm]);

  const sortedKgs = useMemo(() => {
    return [...filteredKgs].sort((a, b) => {
      let av: any = a[sortKey as keyof KGWithStats] ?? '';
      let bv: any = b[sortKey as keyof KGWithStats] ?? '';
      if (sortKey === 'totalVersions') {
        av = Number(av);
        bv = Number(bv);
      } else if (sortKey === 'latestCreatedAt') {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      } else {
        av = String(av).toLowerCase();
        bv = String(bv).toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredKgs, sortKey, sortDir]);

  const totalPages = Math.ceil(sortedKgs.length / pageSize);
  const pagedKgs = sortedKgs.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  const allOnPageSelected = pagedKgs.length > 0 && pagedKgs.every(kg => selectedIds.has(kg.kg_id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pagedKgs.forEach(kg => next.delete(kg.kg_id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pagedKgs.forEach(kg => next.add(kg.kg_id));
        return next;
      });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sortIcon = (key: typeof sortKey) => {
    if (sortKey !== key) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon sort-icon--active">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const copyId = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id).then(() => {
      setCopyToast(id);
      setTimeout(() => setCopyToast(null), 1800);
    });
  };

  const totalPublished = kgs.reduce((sum, k) => sum + k.publishedVersions, 0);

  const skeletonRows = Array.from({ length: 6 }).map((_, i) => (
    <tr key={i} className="tab-skeleton-row">
      <td><div className="tab-skeleton" style={{ width: '60%', height: 14 }} /></td>
      <td><div className="tab-skeleton" style={{ width: '80%', height: 14 }} /></td>
      <td><div className="tab-skeleton" style={{ width: 40, height: 14 }} /></td>
      <td><div className="tab-skeleton" style={{ width: '70%', height: 14 }} /></td>
      <td><div className="tab-skeleton" style={{ width: 60, height: 20, borderRadius: 9999 }} /></td>
      <td><div className="tab-skeleton" style={{ width: 24, height: 24, borderRadius: 4 }} /></td>
    </tr>
  ));

  return (
    <div className="kg-list-container">


      {/* ── Toolbar ── */}
      <div className="kg-list-toolbar">
        <div className="kg-toolbar-left">
          <input
            type="text"
            className="tab-search"
            placeholder="Search knowledge graphs…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
          />
          {!loading && (
            <span className="tab-stat-pill">
              {filteredKgs.length} of {kgs.length}
            </span>
          )}
        </div>
        <div className="kg-toolbar-right">
          <button
            className="tab-btn tab-btn--ghost kg-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Refresh"
          >
            <svg
              className={`refresh-icon${refreshing ? ' refresh-icon--spinning' : ''}`}
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            Create Knowledge Graph
          </button>
        </div>
      </div>

      {error && (
        <div className="tab-banner tab-banner--error">
          <span className="tab-banner-icon">⚠</span>
          <span className="tab-banner-body">{error}</span>
        </div>
      )}

      {/* ── Bulk action bar ── */}
      {someSelected && (
        <div className="kg-bulk-bar">
          <span className="kg-bulk-count">{selectedIds.size} selected</span>
          <button className="tab-btn tab-btn--ghost" style={{ fontSize: 12 }} onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {/* ── Table ── */}
      {!loading && kgs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <ellipse cx="12" cy="5" rx="9" ry="3" />
              <path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
              <path d="M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" />
            </svg>
          </div>
          <p className="empty-state-title">No knowledge graphs yet</p>
          <p className="empty-state-sub">Create your first KG to get started</p>
          <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
            Create Knowledge Graph
          </button>
        </div>
      ) : !loading && filteredKgs.length === 0 ? (
        <div className="empty-state">
          <p className="empty-state-title">No results</p>
          <p className="empty-state-sub">No knowledge graphs match "{searchTerm}"</p>
          <button className="tab-btn tab-btn--ghost" onClick={() => setSearchTerm('')}>Clear search</button>
        </div>
      ) : (
        <div className="kg-table-wrapper sticky-thead">
          <table className="kg-table">
            <thead>
              <tr>
                <th className="kg-th-sort" onClick={() => handleSort('kg_name')}>
                  Knowledge Graph {sortIcon('kg_name')}
                </th>
                <th className="kg-th-sort" onClick={() => handleSort('totalVersions')}>
                  Versions {sortIcon('totalVersions')}
                </th>
                <th className="kg-th-sort" onClick={() => handleSort('latestCreatedAt')}>
                  Latest {sortIcon('latestCreatedAt')}
                </th>
                <th>GDS</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                skeletonRows
              ) : (
                pagedKgs.map((kg) => (
                  <tr
                    key={kg.kg_id}
                    className={`kg-table-row${selectedIds.has(kg.kg_id) ? ' kg-table-row--selected' : ''}`}
                    onClick={() => navigate(`/kg/${kg.kg_id}`)}
                  >
                    {/* Name + ID combined */}
                    <td className="kg-name-cell">
                      <div className="kg-name-primary">{kg.kg_name !== kg.kg_id ? kg.kg_name : kg.kg_id}</div>
                      {kg.kg_name !== kg.kg_id && (
                        <div
                          className="kg-name-id mono-sm copy-cell"
                          onClick={(e) => copyId(kg.kg_id, e)}
                          title="Click to copy ID"
                        >
                          {kg.kg_id}
                          <span className="copy-hint">{copyToast === kg.kg_id ? '✓ copied' : 'copy'}</span>
                        </div>
                      )}
                    </td>

                    {/* Versions */}
                    <td>
                      <div className="versions-cell">
                        <span className="vc-total">{kg.totalVersions}</span>
                        {kg.publishedVersions > 0 && (
                          <span className="status-pill status-pill--published">{kg.publishedVersions} pub</span>
                        )}
                        {kg.unpublishedVersions > 0 && (
                          <span className="status-pill status-pill--draft">{kg.unpublishedVersions} draft</span>
                        )}
                      </div>
                    </td>

                    {/* Latest */}
                    <td>
                      <div className="latest-cell">
                        {kg.latestVersionId ? (
                          <>
                            <span className="lv-ver mono-sm">v{kg.latestVersionId}</span>
                            {kg.latestDataVersion && (
                              <span className="mono-sm" style={{ color: 'var(--muted-foreground)' }}>
                                {kg.latestDataVersion}
                              </span>
                            )}
                            {kg.latestCreatedAt && (() => {
                              const rt = relativeTime(kg.latestCreatedAt);
                              return (
                                <span className="lv-date" title={rt.absolute}>
                                  {rt.relative}
                                </span>
                              );
                            })()}
                          </>
                        ) : (
                          <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                        )}
                      </div>
                    </td>

                    {/* GDS */}
                    <td>
                      {kg.gdsEnabled
                        ? <span className="status-pill status-pill--published">Enabled</span>
                        : <span style={{ color: 'var(--muted-foreground)' }}>—</span>
                      }
                    </td>

                    {/* Overflow menu */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <div
                        className="overflow-menu-wrap"
                        ref={openMenuId === kg.kg_id ? menuRef : null}
                      >
                        <button
                          className="overflow-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(openMenuId === kg.kg_id ? null : kg.kg_id);
                          }}
                          aria-label="Row actions"
                        >
                          ⋯
                        </button>
                        {openMenuId === kg.kg_id && (
                          <div className="overflow-menu" style={{ right: 0, left: 'auto' }}>
                            <button
                              className="overflow-menu-item"
                              onClick={() => { setOpenMenuId(null); navigate(`/kg/${kg.kg_id}`); }}
                            >
                              View details
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ── Pagination ── */}
          {!loading && totalPages > 1 && (
            <div className="kg-pagination">
              <div className="kg-pagination-info">
                Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sortedKgs.length)} of {sortedKgs.length}
              </div>
              <div className="kg-pagination-controls">
                <select
                  className="kg-page-size-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                >
                  {PAGE_SIZE_OPTIONS.map(s => (
                    <option key={s} value={s}>{s} per page</option>
                  ))}
                </select>
                <button
                  className="tab-btn tab-btn--ghost"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  className="tab-btn tab-btn--ghost"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateKGModal onClose={() => setShowCreateModal(false)} onCreated={handleKGCreated} hasGdsLicense={hasGdsLicense} />
      )}

    </div>
  );
};

export default KGList;
